import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, X } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../components/Input/Input";
import Loader from "../../../components/Loader/Loader";
import Notification from "../../../components/Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiGet, apiPut } from "../../../apiService";
import { getErrorMessage } from "../../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./AdminEditOrganization.module.scss";

const EDITABLE_FIELDS = [
  "industry",
  "companyName",
  "state",
  "address",
  "gstNo",
  "minimumFundsValue",
  "subDomain",
  "maxUsersMode",
  "maxUsersValue",
];

const INITIAL_FORM = {
  industry: "",
  companyName: "",
  state: "",
  address: "",
  gstNo: "",
  minimumFundsValue: "",
  subDomain: "",
  maxUsersMode: "unlimited",
  maxUsersValue: "",
};

const MAX_USERS_OPTIONS = [
  { label: "Unlimited", value: "unlimited" },
  { label: "Custom", value: "custom" },
];

const BACK_PATH = "/admin/organization";

const AdminEditOrganization = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* =========================
     DATA LOADING
  ========================= */
  const fetchMasterData = useCallback(async () => {
    try {
      const [industryRes, stateRes] = await Promise.all([
        apiGet("user", "/master/industry"),
        apiGet("user", "/master/state"),
      ]);
      setIndustryOptions(industryRes?.data || []);
      setStateOptions(stateRes?.data || []);
    } catch (err) {
      console.error("Failed to fetch master data", err);
    }
  }, []);

  const fetchOrganization = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await apiGet("user", `/organizations/${id}`);
      if (res?.status !== "success") {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to load organization",
          }),
        );
      }
      const data = res.data || {};
      setOrganization(data);
      const isUnlimited =
        data.maxUsers === "unlimited" ||
        data.maxUsers === null ||
        data.maxUsers === undefined;
      setForm({
        industry: data.industry ?? "",
        companyName: data.companyName ?? "",
        state: data.state ?? "",
        address: data.address ?? "",
        gstNo: data.gstNo ?? "",
        minimumFundsValue: data.minimumFundsValue ?? "",
        subDomain: data.subDomain ?? "",
        maxUsersMode: isUnlimited ? "unlimited" : "custom",
        maxUsersValue: isUnlimited ? "" : String(data.maxUsers),
      });
    } catch (err) {
      showNotification(
        getErrorMessage(err, "Failed to load organization"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMasterData();
  }, [fetchMasterData]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  /* =========================
     FORM HANDLERS
  ========================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!EDITABLE_FIELDS.includes(name)) return;
    // Max users (custom) accepts whole numbers only.
    const nextValue =
      name === "maxUsersValue" ? value.replace(/[^0-9]/g, "") : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!String(form.companyName).trim()) {
      next.companyName = "Company name is required";
    }
    if (!form.industry) {
      next.industry = "Industry is required";
    }
    if (!String(form.state).trim()) {
      next.state = "State is required";
    }
    if (!String(form.address).trim()) {
      next.address = "Address is required";
    }
    if (!String(form.gstNo).trim()) {
      next.gstNo = "GST number is required";
    }
    if (
      form.minimumFundsValue === "" ||
      form.minimumFundsValue === null ||
      Number.isNaN(Number(form.minimumFundsValue)) ||
      Number(form.minimumFundsValue) <= 0
    ) {
      next.minimumFundsValue = "Enter a valid minimum funds value";
    }
    if (form.maxUsersMode === "custom") {
      const num = Number(form.maxUsersValue);
      if (
        form.maxUsersValue === "" ||
        !Number.isInteger(num) ||
        num <= 0
      ) {
        next.maxUsersValue = "Enter a valid number of users";
      }
    }
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        industry: Number(form.industry),
        companyName: String(form.companyName).trim(),
        state: String(form.state).trim(),
        address: String(form.address).trim(),
        gstNo: String(form.gstNo).trim(),
        minimumFundsValue: Number(form.minimumFundsValue),
        subDomain: String(form.subDomain).trim(),
        maxUsers:
          form.maxUsersMode === "unlimited"
            ? "unlimited"
            : Number(form.maxUsersValue),
      };

      const res = await apiPut("user", `/organizations/${id}`, payload);
      if (res?.status === "success") {
        showNotification(
          res?.message || "Organization updated successfully",
          "success",
        );
        setTimeout(() => navigate(BACK_PATH), 1200);
      } else {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to update organization",
          }),
        );
      }
    } catch (err) {
      showNotification(
        getErrorMessage(err, "Failed to update organization"),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className={styles["edit-org__page"]}>
      {notification && (
        <div className={styles["edit-org__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      <div className={styles["edit-org__header"]}>
        <Link to={BACK_PATH} className={styles["edit-org__back-btn"]}>
          <ArrowLeft size={20} strokeWidth={2.5} />
        </Link>
        <div className={styles["edit-org__title-group"]}>
          <h2 className={styles["edit-org__title"]}>Edit Organization</h2>
          <p className={styles["edit-org__subtitle"]}>
            Update company information and billing thresholds.
          </p>
        </div>
      </div>

      <div className={styles["edit-org__card"]}>
        <div className={styles["edit-org__meta"]}>
          <div>
            <span>Owner</span>
            <strong>{organization?.name || "--"}</strong>
          </div>
          <div>
            <span>Email</span>
            <strong>{organization?.email || "--"}</strong>
          </div>
          <div>
            <span>Status</span>
            <strong>{organization?.status || "--"}</strong>
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className={styles["edit-org__grid"]}>
            <Input
              label="Company Name"
              name="companyName"
              placeholder="Enter company name"
              value={form.companyName}
              onChange={handleChange}
              error={errors.companyName}
              required
            />

            <Input
              label="Industry"
              type="select"
              name="industry"
              placeholder="Select industry"
              value={form.industry}
              onChange={handleChange}
              error={errors.industry}
              required
              options={industryOptions.map((opt) => ({
                label: opt.label,
                value: opt.value,
              }))}
            />

            <Input
              label="State"
              type="select"
              name="state"
              placeholder="Select state"
              value={form.state}
              onChange={handleChange}
              error={errors.state}
              required
              options={stateOptions.map((opt) => ({
                label: opt.label,
                value: opt.label,
              }))}
            />

            <Input
              label="GST Number"
              name="gstNo"
              placeholder="Enter GST number"
              value={form.gstNo}
              onChange={handleChange}
              error={errors.gstNo}
              required
            />

            <Input
              label="Address"
              type="textarea"
              name="address"
              placeholder="Enter full address"
              value={form.address}
              onChange={handleChange}
              error={errors.address}
              required
              className={styles["edit-org__field--full"]}
            />

            <Input
              label="Minimum Funds Value (₹)"
              type="number"
              name="minimumFundsValue"
              placeholder="e.g. 60000"
              value={form.minimumFundsValue}
              onChange={handleChange}
              error={errors.minimumFundsValue}
              required
              min={0}
            />

            <Input
              label="VerifyX Subdomain"
              name="subDomain"
              placeholder="e.g. acme"
              value={form.subDomain}
              onChange={handleChange}
              error={errors.subDomain}
            />

            <Input
              label="Max Users"
              type="select"
              name="maxUsersMode"
              value={form.maxUsersMode}
              onChange={handleChange}
              options={MAX_USERS_OPTIONS}
              required
            />

            {form.maxUsersMode === "custom" && (
              <Input
                label="Max Users Value"
                type="number"
                name="maxUsersValue"
                placeholder="e.g. 25"
                value={form.maxUsersValue}
                onChange={handleChange}
                error={errors.maxUsersValue}
                required
                min={1}
                step={1}
              />
            )}
          </div>

          <div className={styles["edit-org__actions"]}>
            <button
              type="button"
              className={`${styles["edit-org__btn"]} ${styles["edit-org__btn--secondary"]}`}
              onClick={() => navigate(BACK_PATH)}
              disabled={submitting}
            >
              <X size={16} strokeWidth={2.5} />
              Cancel
            </button>

            <button
              type="submit"
              className={`${styles["edit-org__btn"]} ${styles["edit-org__btn--primary"]}`}
              disabled={submitting}
            >
              <Save size={16} strokeWidth={2.5} />
              {submitting ? "Updating…" : "Update Organization"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminEditOrganization;
